'use client';

import React from 'react';
import Link from 'next/link';
import { useTranslation, Trans } from 'react-i18next';
import { buttonVariants } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import {
  Download,
  Mail,
  Image as ImageIcon,
  FileText,
  Sparkles,
  Users,
  DollarSign,
  Calendar,
  Building2,
  ArrowRight,
} from 'lucide-react';

// Three facts, not four. The fourth slot held "60+ countries" (unverifiable),
// then "4,000+ investment firms" (measured, but a second database number reads
// as a competing claim), then an "Unlimited" web-discovery tile (an overclaim —
// plans reach 10/50/150 investors a month, PRICING_TIERS in types/index.ts).
// Owner decision 2026-08-25: one database figure everywhere, 5,000+ investors.
// See lib/approx.ts before adding anything back here.
const FACT_KEYS = [
  { key: 'verified', icon: Users },
  { key: 'pricing', icon: DollarSign },
  { key: 'launched', icon: Calendar },
] as const;

const ASSET_KEYS = [
  { key: 'logo', href: '/logo.svg', icon: ImageIcon },
  // Raster copies of the same mark, for outlets that cannot take SVG. Both are
  // produced by scripts/generate-logo-rasters.mjs from logo.svg — PNG keeps the
  // transparent surround, JPG is flattened onto white for upload forms that
  // reject alpha. Re-run that script rather than hand-editing either file.
  { key: 'logoPng', href: '/logo.png', icon: ImageIcon },
  { key: 'logoJpg', href: '/logo.jpg', icon: ImageIcon },
  // The full lockup (mark + "SendVC" wordmark on white). Rendered by
  // scripts/generate-ad-images.mjs from public/logo.svg itself, so it is the
  // same mark this list's first entry serves — not a redrawn one.
  { key: 'logoLockup', href: '/ads/logo-landscape-1200x300.png', icon: ImageIcon },
  { key: 'icon', href: '/icon-512.png', icon: ImageIcon },
  { key: 'og', href: '/og-image.png', icon: ImageIcon },
  { key: 'twitter', href: '/twitter-image.png', icon: ImageIcon },
] as const;

const BOILERPLATE_KEYS = ['short', 'medium', 'long'] as const;

export default function PressContent() {
  const { t } = useTranslation();
  const topics = t('pageContent.press.topics.items', { returnObjects: true }) as unknown;
  const topicsList: string[] = Array.isArray(topics) ? topics : [];

  return (
    <>
      {/* Hero */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block bg-primary-50 text-primary-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              {t('pageContent.press.hero.badge')}
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              {t('pageContent.press.hero.title')}
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              {t('pageContent.press.hero.subtitle')}
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <a href="mailto:info@sendvc.app" className={buttonVariants({ variant: 'default', size: 'lg' })}>
                <Mail className="mr-2 h-4 w-4" />
                {t('pageContent.press.hero.emailCta')}
              </a>
              <a href="#assets" className={buttonVariants({ variant: 'outline', size: 'lg' })}>
                <Download className="mr-2 h-4 w-4" />
                {t('pageContent.press.hero.assetsCta')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Fact sheet */}
      <section className="py-16 md:py-20">
        <div className="container">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">
              {t('pageContent.press.facts.heading')}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {FACT_KEYS.map((fact) => (
                <div
                  key={fact.key}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center"
                >
                  <fact.icon className="h-8 w-8 text-primary-500 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-gray-900 mb-1">
                    {t(`pageContent.press.facts.${fact.key}.value`)}
                  </div>
                  <div className="text-sm text-gray-500">
                    {t(`pageContent.press.facts.${fact.key}.label`)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Boilerplate */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">
              {t('pageContent.press.boilerplate.heading')}
            </h2>
            <p className="text-center text-gray-600 mb-12">
              {t('pageContent.press.boilerplate.intro')}
            </p>

            <div className="space-y-8">
              {BOILERPLATE_KEYS.map((variant) => (
                <div
                  key={variant}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="inline-flex items-center gap-1.5 text-primary-600 font-semibold text-sm">
                      <FileText className="h-4 w-4" />
                      {t(`pageContent.press.boilerplate.${variant}.label`)}
                    </span>
                    <span className="text-xs text-gray-400">
                      {t(`pageContent.press.boilerplate.${variant}.hint`)}
                    </span>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    {t(`pageContent.press.boilerplate.${variant}.body`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Assets */}
      <section id="assets" className="py-16 md:py-20">
        <div className="container">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">
              {t('pageContent.press.assets.heading')}
            </h2>
            <p className="text-center text-gray-600 mb-12">
              {t('pageContent.press.assets.intro')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {ASSET_KEYS.map((asset) => (
                <a
                  key={asset.key}
                  href={asset.href}
                  download
                  className="group bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start gap-4">
                    <div className="bg-primary-50 rounded-lg p-3">
                      <asset.icon className="h-6 w-6 text-primary-500" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-primary-600 transition-colors">
                        {t(`pageContent.press.assets.${asset.key}.name`)}
                      </h3>
                      <p className="text-sm text-gray-600 mb-3">
                        {t(`pageContent.press.assets.${asset.key}.description`)}
                      </p>
                      <span className="inline-flex items-center gap-1 text-primary-500 font-medium text-sm">
                        <Download className="h-3.5 w-3.5" />
                        {t('pageContent.press.assets.downloadLabel')}
                      </span>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Press topics */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">
              {t('pageContent.press.topics.heading')}
            </h2>
            <p className="text-center text-gray-600 mb-12">
              {t('pageContent.press.topics.intro')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {topicsList.map((topic, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex items-start gap-3"
                >
                  <Sparkles className="h-5 w-5 text-primary-500 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm leading-relaxed">{topic}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Original research. Journalists are the audience most likely to cite the
          2026 data report, and /press is where they land — but the report had no
          internal inlinks at all until now (Ahrefs flagged it as an orphan). */}
      <section className="py-16 md:py-20">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              {t('pageContent.press.research.heading')}
            </h2>
            <p className="text-gray-600 mb-8 leading-relaxed">
              {t('pageContent.press.research.description')}
            </p>
            <Link
              href="/reports/state-of-early-stage-vc-2026"
              className={buttonVariants({ variant: 'outline', size: 'lg' })}
            >
              {t('pageContent.press.research.cta')}
            </Link>
          </div>
        </div>
      </section>

      {/* Founder bio */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">
              {t('pageContent.press.about.heading')}
            </h2>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 space-y-4 text-gray-700 leading-relaxed">
              <p>{t('pageContent.press.about.p1')}</p>
              <p>{t('pageContent.press.about.p2')}</p>
              <p>{t('pageContent.press.about.p3')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 md:py-20 bg-primary-500">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <Building2 className="h-12 w-12 text-white mx-auto mb-6" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              {t('pageContent.press.cta.heading')}
            </h2>
            <p className="text-xl text-primary-50 mb-8">
              {/* No `components` prop: <strong> is a default basic-HTML node in
                  react-i18next (transKeepBasicHtmlNodesFor), so it renders
                  natively with proper keys. Passing components={{ strong }} took
                  the mapped-array path and triggered React's "unique key" warning. */}
              <Trans i18nKey="pageContent.press.cta.body" />
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {/* cn() is not cosmetic here: these two override the variant's own
                  colours (bg-white over bg-primary-500, border-white over
                  border-primary-500). Raw cva only concatenates, so both classes
                  would ship and the stylesheet order — not the class order —
                  would decide the winner. cn()'s twMerge drops the losing
                  utility, exactly as the Button component did internally. */}
              <a
                href="mailto:info@sendvc.app"
                className={cn(buttonVariants({ variant: 'default', size: 'lg', className: 'bg-white text-primary-600 hover:bg-gray-50' }))}
              >
                <Mail className="mr-2 h-4 w-4" />
                {t('pageContent.press.cta.emailCta')}
              </a>
              <Link
                href="/contact"
                className={cn(buttonVariants({ variant: 'outline', size: 'lg', className: 'bg-transparent text-white border-white hover:bg-white hover:text-primary-600' }))}
              >
                {t('pageContent.press.cta.otherCta')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
